import {Component, computed, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ResourceParams} from "../../models/resource/resources";
import {defaultResourceParams} from "../../models/resource/default-resource-params";
import {
  FiltersValues,
  RecordData,
  RecordTableColumnParams,
  RecordTotalData,
  RecordUpdateParams
} from "../../types/records-table.types";
import {
  RecordsTablePaginatorComponent
} from "../records-table-view/components/records-table-paginator/records-table-paginator.component";
import {
  RecordsDataRequestQueryParamsService
} from "./services/records-data-request-query-params.service";
import {RecordTableApiService} from "../../services-api/record-table-api.service";
import {RecordsTableService} from "../../services/records-table.service";
import {ModalsService} from "../../services/modals.service";
import {LoadingService} from "../../services/loading.service";
import {ErrorService} from "../../services/error.service";
import {UserPreferencesService} from "../../services/user-preferences.service";
import {ResizeService} from "../../services/resize.service";
import {RecordTableOpenModalService} from "./services/record-table-open-modal.service";
import {RecordTableActionsService} from "./services/record-table-actions.service";
import {RecordTableAction} from "../../types/record-table-action.types";
import {Pageable} from "../../types/pagable.types";
import {lastValueFrom, Subscription} from "rxjs";
import {UpdateQueryParamsProps} from "./types/record-table-container.types";
import {shouldReloadTable} from "../records-table-view/configs/records-table-config";
import {defaultPageableRecordData} from "../../models/default-pageable-record-data";
import {UserPreferences} from "../../types/user-preferences.types";
import {defaultRecordTableParams} from "../../models/default-record-table-params";
import {CommonModule} from "@angular/common";
import {RecordsTableModule} from "../records-table/records-table.module";
import {RecordsTableViewComponent} from "../records-table-view/records-table-view.component";
import {SelectedRowStateService} from "../../services/table-services/selected-row-state.service";
import {RecordsTableToolbarComponent} from "../records-table-toolbar/records-table-toolbar.component";
import {RecordsTableFiltersComponent} from "../records-table-filters/records-table-filters.component";
import {ModalStatesService} from "../../services/table-services/modal-states.service";
import {RecordsTableContainerService} from "./services/records-table-container.service";
import {DEFAULT_ZOOM} from "../../constants/constants";
import {ToggleAllGroupsVisibilityService} from "../../services/table-services/toggle-all-groups-visibility.service";
import {SnackbarService} from "../../services/snackbar.service";
import {LoaderComponent} from "../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-records-table-container',
  imports: [
    CommonModule,
    RecordsTablePaginatorComponent,
    RecordsTableModule,
    RecordsTableViewComponent,
    RecordsTableToolbarComponent,
    RecordsTableFiltersComponent,
    LoaderComponent
  ],
  templateUrl: './records-table-container.component.html',
  styleUrl: './records-table-container.component.scss',
  providers: [ LoadingService ]
})
export class RecordsTableContainerComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) resourceParams: ResourceParams = defaultResourceParams;
  @Input() defaultFilters: FiltersValues = {};
  @Input() defaultSort: Array<string> = [];
  @Input() defaultNewRecordFields: { [key: string]: any } = {};
  @Input() isFullHeight: boolean = true;
  @Input() isProductCardFullHeight: boolean = false;
  @ViewChild('recordsTableViewComponent') recordsTableViewComponent!: RecordsTableViewComponent;
  @ViewChild('controlsComponent') controlsComponent: RecordsTableToolbarComponent | null = null;
  @ViewChild('paginatorComponent') paginatorComponent: RecordsTablePaginatorComponent | null = null;

  public recordsDataRequestQueryParamsService: RecordsDataRequestQueryParamsService = inject(RecordsDataRequestQueryParamsService);
  public selectedRowStateService: SelectedRowStateService = inject(SelectedRowStateService);
  private recordTableApiService: RecordTableApiService = inject(RecordTableApiService);
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private modalsService: ModalsService = inject(ModalsService);
  private loadingService: LoadingService = inject(LoadingService);
  private errorService: ErrorService = inject(ErrorService);
  private userPreferencesService: UserPreferencesService = inject(UserPreferencesService);
  private recordsTableContainerService: RecordsTableContainerService = inject(RecordsTableContainerService);
  private resizeService: ResizeService = inject(ResizeService);
  private recordTableOpenModalService: RecordTableOpenModalService = inject(RecordTableOpenModalService);
  private recordTableActionsService: RecordTableActionsService = inject(RecordTableActionsService);
  private modalStatesService: ModalStatesService = inject(ModalStatesService);
  private toggleAllGroupsVisibilityService: ToggleAllGroupsVisibilityService = inject(ToggleAllGroupsVisibilityService);
  private snackbarService: SnackbarService = inject(SnackbarService);

  public recordsTableActions: Array<RecordTableAction> = [];
  public recordsTableData: Pageable<RecordData> | null = null;
  public recordsTotalData: Array<RecordTotalData> = [];
  public recordsTableColumnsParams: Array<RecordTableColumnParams> = [];
  public isLoading: boolean = false;
  public currentZoomOption: number = DEFAULT_ZOOM;
  public hasFilters: boolean = false;
  private isShowProcessedUserAction: boolean = false;
  private subscriptions: Subscription = new Subscription();

  public isResizeColumn = computed(() => this.resizeService.isResizingSignal()[this.resourceParams.key] ?? false);

  public async ngOnInit() {
    this.prepareDefaultQueryParams();
    this.initSubscriptions();
  }

  public async ngOnChanges(changes: SimpleChanges): Promise<void> {
    changes['resourceParams'] && this.resizeService.resetResizingState();
    await this.initRecordTable();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public reloadTableData(): void {
    this.updateRecordTableData().then();
  }

  public updateQueryParams({ key, value }: UpdateQueryParamsProps): void {
    this.recordsDataRequestQueryParamsService.updateQueryParams({
      updateQueryParamsProps: { key, value }
    });

    key === 'showProcessed' && (this.isShowProcessedUserAction = value as boolean);
    this.updateRecordTableData().then();
  }

  public changeFiltersParams(recordsTableFiltersValues: FiltersValues): void {
    this.recordsDataRequestQueryParamsService.changeFiltersParams({
      recordsTableFiltersValues: recordsTableFiltersValues,
      isShowProcessedUserAction: this.isShowProcessedUserAction
    });

    this.updateRecordTableData().then();
  }

  public toggleFiltersVisible(): void {
    this.handleZoomChange(this.currentZoomOption);
  }

  public changeSortParams(sortParams: string): void {
    this.recordsDataRequestQueryParamsService.changeSortParams({
      sortParams: sortParams
    });

    this.updateRecordTableData().then();
  }

  public async handleRecordUpdate(recordUpdateParams: RecordUpdateParams): Promise<void> {
    if (!this.resourceParams) { return; }

    this.loadingService.setIsLoadingState(true);
    const showMistakes = this.recordsDataRequestQueryParamsService.recordsDataRequestQueryParamsState().showMistakes;
    const recordData = await this.recordsTableContainerService.updatedRecordField(recordUpdateParams, this.resourceParams.path);

    this.updateRecordInCurrentRecordData(recordData);
    this.loadingService.setIsLoadingState(false);
    if (!showMistakes) { return; }

    await this.updateRecordTableData();
  }

  public async handleRecordDelete(recordId: string | number): Promise<void> {
    const isConfirmed = await this.recordsTableContainerService.isDeleteConfirmed(recordId);
    if (!isConfirmed) { return; }

    this.recordTableApiService.deleteRecord(this.resourceParams?.path!, recordId).subscribe({
      next: () => this.reloadTableData(),
      error: (error) => this.errorService.handleError(error)
    });
  }

  public async handleActionSelect(action: RecordTableAction): Promise<void> {
    switch (action) {
      case "RESIZE_COLUMN":
        if (this.recordsTableViewComponent?.isAddNewRecordStateActive) {
          this.snackbarService.infoShowMessage('Завершите создание записи, чтобы включить изменение размеров колонок!');
          return;
        }

        if (this.recordsTableViewComponent?.isEditModeActive) {
          this.snackbarService.infoShowMessage('Завершите редактирование, чтобы включить изменение размеров колонок!');
          return;
        }

        this.setIsResizing(!this.isResizeColumn());
        return;
      case 'EXPAND_GROUP':
      case 'COLLAPSE_GROUP':
        this.toggleAllGroupsVisibilityService.toggleGroupAllVisibility({
          key: this.resourceParams.key,
          action: action
        });
        return;
      case 'UPLOAD_PRODUCT_PACKAGES_EXCEL':
        await this.modalsService.openProductPackagesModal('uploadProductPackagesExcel');
        break;
      case 'DOWNLOAD_PRODUCT_PACKAGES_EXCEL':
        await this.modalsService.openProductPackagesModal('downloadProductPackagesExcel');
        break;
      default:
        await this.recordTableActionsService.handleActionSelect(action, {
          resourceParams: this.resourceParams,
          queryParams: this.recordsDataRequestQueryParamsService.recordsDataRequestQueryParamsState(),
          selectedRowId: this.selectedRowStateService.selectedRowId(),
          recordsTableData: this.recordsTableData,
          fields: this.recordsTableColumnsParams
        });
    }

    shouldReloadTable(action) && this.reloadTableData();
  }

  public handleEditModeStateChange(isEditModeActive: boolean): void {
    this.recordsTableViewComponent.handleEditModeStateChange(isEditModeActive);
  }

  public handleAddRecordStateChange(isAddingNewRecord: boolean): void {
    this.recordsTableViewComponent.handleAddRecordStateChange(isAddingNewRecord);
  }

  public handleAddCooperationOffer(): void {
    this.modalsService.openCooperationOffer()
      .afterClosed()
      .subscribe(() => this.reloadTableData());
  }

  public handleAddTelegramAccount(): void {
    this.modalsService.openAddTelegramAccount()
      .afterClosed()
      .subscribe(() => this.reloadTableData());
  }

  public handleAddUser(): void {
    this.modalsService.openAddUserModal()
      .afterClosed()
      .subscribe(() => this.reloadTableData());
  }

  public async handleAddPriceLevelConfig(): Promise<void> {
    await this.modalsService.openPriceLevelConfigsModal('createPriceLevelConfigsModal');
    this.reloadTableData();
  }

  public async handleCreateBloggerGroup(): Promise<void> {
    await this.recordTableOpenModalService.openCreateBloggerGroup();
    await this.initRecordTable();
  }

  public async handleOpenBloggerGroups(): Promise<void> {
    await this.modalsService.openBloggerGroupsModal(null, 'currentBloggerGroups');
    await this.initRecordTable();
  }

  public async handleSendMassOffers(): Promise<void> {
    await this.recordTableOpenModalService.sendMassOffers();
  }

  public async handleCreateComponent(): Promise<void> {
    const result = await this.modalsService.openCreateComponentModal(this.defaultFilters['productCardId'] as string || '');
    result === 'success' && this.reloadTableData();
  }

  public async handleCreateTestingResult(): Promise<void> {
    const result = await this.modalsService.openCreateTestingResultModal(this.defaultFilters['productCardId'] as string || '');
    result === 'success' && this.reloadTableData();
  }

  public async handleCreateQualityControl(): Promise<void> {
    const productCardId = this.defaultFilters['productCardId'] as string || '';
    const result = await this.modalsService.openCreateQualityControlModal('createQualityControl', productCardId, this.recordsTableColumnsParams);
    result === 'success' && this.reloadTableData();
  }

  public async saveNewRecord(): Promise<void> {
    await this.handleNewRecordSave(this.recordsTableViewComponent.newRecordData);
  }

  public handleZoomChange(zoomOption: number, isNeedToUpdateUserPreferences: boolean = true): void {
    setTimeout(() => {
      this.recordsTableViewComponent?.handleZoomChange(zoomOption);
      this.currentZoomOption = zoomOption;

      if (isNeedToUpdateUserPreferences) {
        this.userPreferencesService.saveUserPreferences({ tableScale: zoomOption });
      }
    });
  }

  public isNewRecordDataValid(): boolean {
    return !!this.recordsTableViewComponent?.isNewRecordDataValid;
  }

  public async updateRecordTableData(): Promise<void> {
    if (!this.resourceParams) { return; }

    if (this.modalStatesService.isBloggerProfileOpen()) {
      await this.initRecordTable();
      return;
    }

    this.loadingService.setIsLoadingState(true);
    this.recordsTableData = await this.recordsTableContainerService.getRecordsTableData(this.resourceParams.path);
    this.recordsTotalData = await this.recordsTableContainerService.getRecordsTotalData(this.resourceParams.path, this.recordsTableActions);
    this.resourceParams.path === 'bloggers' &&
      this.recordsTableContainerService.setColumnCheckboxState(this.recordsTableData.content);
    this.loadingService.setIsLoadingState(false);
  }

  public async refreshTable(): Promise<void> {
    this.recordsTableData = defaultPageableRecordData;
    this.recordsTotalData = [];
    await this.updateRecordTableData();
  }

  private prepareDefaultQueryParams(): void {
    this.recordsDataRequestQueryParamsService.prepareDefaultQueryParams({
      resourceParams: this.resourceParams,
      defaultFilters: this.defaultFilters,
      defaultSort: this.defaultSort
    });
  }

  private async initRecordTable(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    await this.updateRecordsTableParams();
    this.prepareDefaultQueryParams();
    this.paginatorComponent?.resetPaginator();
    if (this.modalStatesService.isBloggerProfileOpen()) {
      this.modalStatesService.closeBloggerModalOpen();
      this.handleUserPreferences();
      this.loadingService.setIsLoadingState(false);
    }

    if (this.modalStatesService.isBloggerProfileOpen()) { return; }
    await this.updateRecordTableData();
    this.handleUserPreferences();
    this.loadingService.setIsLoadingState(false);
  }

  private handleUserPreferences(): void {
    const userPreferences: UserPreferences | null = this.userPreferencesService.getUserPreferences();
    if (!userPreferences) {
      this.handleZoomChange(DEFAULT_ZOOM, false);
      return;
    }

    const tableScale: number = userPreferences.tableScale ?? 100;
    this.handleZoomChange(tableScale, false);
  }

  private async updateRecordsTableParams(): Promise<void> {
    if (!this.resourceParams) { return; }

    const recordTableParams = await this.recordsTableService.getRecordTableColumnsParams(this.resourceParams?.path).catch(() => defaultRecordTableParams);
    this.recordsTableColumnsParams = recordTableParams.fields.filter(field => !field.omit);
    this.recordsTableActions = recordTableParams.actions;
    recordTableParams?.properties?.UI_CONFIGURATION?.key && this.setIsResizing(false);
    this.recordsTableActions = this.recordsTableService.updateRecordsTableActions({
      recordTableParamsKey: recordTableParams?.properties?.UI_CONFIGURATION?.key,
      recordsTableActions: this.recordsTableActions,
      recordsTableColumnsParams: this.recordsTableColumnsParams,
      resourceParamsPath: this.resourceParams.path
    });

    this.hasFilters = this.recordsTableContainerService.hasFilterableColumns(this.recordsTableColumnsParams);
    this.userPreferencesService.handleTableProperties(recordTableParams?.properties);
  }

  private updateRecordInCurrentRecordData(updatedRecord: RecordData): void {
    if (!this.recordsTableData) { return; }

    const updatedContent = this.recordsTableData.content.map(record =>
      record.id === updatedRecord.id
        ? { ...record, ...updatedRecord }
        : record
    );

    this.recordsTableData = {
      ...this.recordsTableData,
      content: updatedContent
    };
  }

  private initSubscriptions() {
    this.subscriptions.add(
      this.loadingService.loadingState$.subscribe({
        next: (isLoading: boolean) => this.setIsLoading(isLoading)
      })
    );
  }

  private setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  private setIsResizing(isResizing: boolean) {
    this.resizeService.toggleResizingState(this.resourceParams.key, isResizing);
  }

  private async handleNewRecordSave(newRecordData: Partial<RecordData>): Promise<void> {
    const recordData = await lastValueFrom(this.recordTableApiService.addNewRecord(this.resourceParams.path, { ...newRecordData, ...this.defaultNewRecordFields }));
    const bloggerIdColumn = this.recordsTableColumnsParams.find(column => column.key === 'bloggerId');
    if (bloggerIdColumn) {
      await this.modalsService.getStatusFromBloggerCardModal({
        columnParamKey: 'blogger',
        columnParam: bloggerIdColumn,
        recordData: recordData,
        path: this.resourceParams.path
      });
    }

    this.reloadTableData();
  }
}
